import { type AxisScale, type SeriesProps, LineSeries as VisxLineSeries } from '@visx/xychart';

export type LineVariant = 'dashed' | 'solid';

export const getLineVariantProps = (variant?: LineVariant) => {
  const baseProps = { strokeWidth: 2 };
  return variant === 'dashed' ? { ...baseProps, strokeDasharray: '4 4' } : baseProps;
};

/**
 * Renders a line series with solid or dashed styling.
 *
 * Use this for plotting data as a continuous line. Supports both solid and dashed variants,
 * useful for differentiating multiple series (e.g., actual vs. projected values).
 *
 * @example
 * ```tsx
 * <XYChart width={500} height={300}>
 *   <LineSeries
 *     data={actualData}
 *     dataKey="actual"
 *     xAccessor={d => d.date}
 *     yAccessor={d => d.value}
 *     variant="solid"
 *     color="#1E88E5"
 *   />
 *   <LineSeries
 *     data={projectedData}
 *     dataKey="projected"
 *     xAccessor={d => d.date}
 *     yAccessor={d => d.value}
 *     variant="dashed"
 *     color="#1E88E5"
 *   />
 * </XYChart>
 * ```
 */
export const LineSeries = <XScale extends AxisScale, YScale extends AxisScale, Datum extends object>({
  color,
  data,
  dataKey,
  xAccessor,
  yAccessor,
  variant,
  'data-testid': dataTestId,
}: Pick<SeriesProps<XScale, YScale, Datum>, 'data' | 'dataKey' | 'xAccessor' | 'yAccessor'> & {
  color?: string;
  variant: LineVariant;
  'data-testid'?: string;
}) => {
  const variantProps = getLineVariantProps(variant);
  return (
    <VisxLineSeries
      data={data}
      dataKey={dataKey}
      xAccessor={xAccessor}
      yAccessor={yAccessor}
      {...(color && { stroke: color })}
      data-testid={dataTestId}
      {...variantProps}
    />
  );
};
